import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { menus } from "../../../services/admin";
import type { MenuItem } from "../../../services/admin";
import type { UserInfo } from "../../../services/auth";
import RIcon from "../../../components/RIcon";

interface MenuTreeGroup {
  id: string;
  name: string;
  icon: string;
  sort: number;
  children: MenuItem[];
  standalone?: boolean;
}

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  user?: UserInfo | null;
}

function buildMenuTree(items: MenuItem[]): MenuTreeGroup[] {
  const enabledItems = items.filter((item) => item.enabled);

  // 从 items 中提取分组（myItems 返回的每项带 group_id 和 group_name）
  const groupMap = new Map<number, { name: string; icon: string; sort: number }>();
  for (const item of enabledItems) {
    if (item.group_id && !groupMap.has(item.group_id)) {
      groupMap.set(item.group_id, {
        name: item.group_name || `分组${item.group_id}`,
        icon: item.group_icon || "ri-folder-line",
        sort: item.group_sort ?? item.group_id,
      });
    }
  }

  // 有分组的菜单按分组归类
  const grouped = Array.from(groupMap.entries())
    .map(([gid, g]) => ({
      id: `g-${gid}`,
      name: g.name,
      icon: g.icon,
      sort: g.sort,
      children: enabledItems
        .filter((item) => item.group_id === gid)
        .sort((a, b) => a.sort - b.sort),
    }))
    .filter((group) => group.children.length > 0)
    .sort((a, b) => a.sort - b.sort);

  // 没有分组的菜单作为顶级独立项（每项一个伪分组，无展开按钮）
  const ungrouped = enabledItems.filter((item) => !item.group_id).sort((a, b) => a.sort - b.sort);
  const standalone = ungrouped.map((item) => ({
    id: `top-${item.id}`,
    name: item.name,
    icon: item.icon || "ri-folder-line",
    sort: -1,
    children: [item],
    standalone: true,
  }));

  return [...standalone, ...grouped];
}

export default function Sidebar({ collapsed, mobileOpen, onClose, onToggle, user: _user }: SidebarProps) {
  const location = useLocation();
  const [menuTree, setMenuTree] = useState<MenuTreeGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadMenus() {
      try {
        const itemsData = await menus.myItems();
        if (cancelled) return;

        const tree = buildMenuTree(itemsData);
        setMenuTree(tree);
        setExpandedGroups(tree.map((g) => g.id));
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load menu data:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadMenus();
    return () => { cancelled = true; };
  }, []);

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const isActive = (path: string) => {
    // Exact match for the path itself
    if (location.pathname === path) return true;
    // For child routes, only match if no sibling menu item has a more specific path
    // This prevents /workbench/content-sub from matching when on /workbench/content-sub/reports
    const allPaths = menuTree.flatMap((g) => g.children.map((c) => c.path));
    const isChildRoute = location.pathname.startsWith(path + "/");
    if (isChildRoute) {
      // Check if there's a more specific menu path that matches
      const hasMoreSpecificMatch = allPaths.some(
        (p) => p !== path && p.startsWith(path + "/") && (location.pathname === p || location.pathname.startsWith(p + "/"))
      );
      return !hasMoreSpecificMatch;
    }
    return false;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-[#1A2B3C] z-40 flex flex-col transition-all duration-300
          ${collapsed ? "w-16" : "w-56"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* Logo */}
        <div className="h-14 flex items-center px-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/a84828b1-10f3-4f8c-851b-b2f4fe56a73c.png"
              alt="协和医疗"
              className="w-8 h-8 object-contain flex-shrink-0 brightness-0 invert"
            />
            {!collapsed && (
              <span className="text-white text-sm font-semibold whitespace-nowrap">协和医疗全域AI</span>
            )}
          </div>
          {mobileOpen && (
            <button onClick={onClose} className="ml-auto text-white/50 hover:text-white lg:hidden cursor-pointer">
              <RIcon name="close-line" className="text-lg" />
            </button>
          )}
        </div>

        {/* Menu — 全部从 DB 拉取，权限受菜单管理控制 */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
          {loading ? (
            <div className="px-2.5 py-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-8 bg-white/5 rounded-lg mb-2 animate-pulse" />
              ))}
            </div>
          ) : (
            menuTree.map((group) => {
              if (group.standalone) {
                const item = group.children[0];
                return (
                  <Link
                    key={group.id}
                    to={item.path}
                    onClick={onClose}
                    title={collapsed ? item.name : undefined}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                      isActive(item.path)
                        ? "bg-[#0EA5A0] text-white"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    } ${collapsed ? "justify-center" : ""}`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <RIcon name="file-line" className={`${item.icon || ""} text-base`} />
                    </div>
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{item.name}</span>
                    )}
                  </Link>
                );
              }
              return (
                <div key={group.id}>
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(group.id)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left group transition-colors ${
                      expandedGroups.includes(group.id) ? "text-white" : "text-white/50 hover:text-white"
                    } cursor-pointer`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${group.icon} text-base`} />
                    </div>
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-xs font-semibold tracking-wide truncate">{group.name}</span>
                        <i className={`ri-arrow-${expandedGroups.includes(group.id) ? "up" : "down"}-s-line text-xs opacity-60`} />
                      </>
                    )}
                  </button>

                  {/* Children — 折叠时不缩进，展开时左边线+缩进体现层级 */}
                  {expandedGroups.includes(group.id) && group.children.length > 0 && (
                    <div className={`mt-0.5 space-y-0.5 ${collapsed ? "" : "ml-3 pl-2 border-l border-white/10"}`}>
                      {group.children.map((item) => (
                        <Link
                          key={item.id}
                          to={item.path}
                          onClick={onClose}
                          title={collapsed ? item.name : undefined}
                          className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-colors ${
                            isActive(item.path)
                              ? "bg-[#0EA5A0] text-white"
                              : "text-white/60 hover:bg-white/10 hover:text-white"
                          } ${collapsed ? "justify-center" : ""}`}
                        >
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <RIcon name="file-line" className={`${item.icon || ""} text-sm`} />
                          </div>
                          {!collapsed && (
                            <span className="text-sm truncate">{item.name}</span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* Collapse Toggle Button */}
        <div className="px-2 py-2 border-t border-white/10">
          <button
            onClick={onToggle}
            title={collapsed ? "展开侧边栏" : "收起侧边栏"}
            className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors cursor-pointer ${collapsed ? "justify-center" : ""}`}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <RIcon name="menu-unfold-line" className={`${collapsed ? "" : ""} text-base`} />
            </div>
            {!collapsed && (
              <span className="text-xs font-medium">收起菜单</span>
            )}
          </button>
        </div>

      </aside>
    </>
  );
}
